/**
 * Reconnect protocol for the Hostesa realtime client.
 *
 * A disconnect — however brief — can mean missed events and a stale cache.
 * On every reconnect the protocol:
 *   1. Re-authenticates: the socket.io-client `auth` callback already handles
 *      this by calling `getToken()` on every handshake, so no extra step is needed.
 *   2. Re-subscribes all rooms that were active at the time of disconnect.
 *   3. Invalidates TanStack Query caches for all subscribed hotel scopes.
 *   4. Lets TanStack Query's refetch-on-focus / window-focus logic pull fresh data.
 *
 * Observable connection state is exposed so UI can show offline/degraded banners
 * without leaking any payload PII.
 */
import type { QueryClient } from '@tanstack/react-query';
import { invalidateHotelScope } from './cache';
import { subscribeCallRoom, subscribeHotelRoom } from './rooms';
import type { RealtimeSocket } from './socket';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

export interface ActiveRooms {
  /** Currently subscribed hotel rooms: tenantId → Set<hotelId> */
  hotels: Map<string, Set<string>>;
  /** Currently subscribed call rooms: callId → { tenantId, hotelId } */
  calls: Map<string, { tenantId: string; hotelId: string }>;
}

export type StatusListener = (status: ConnectionStatus) => void;

// ---------------------------------------------------------------------------
// ReconnectManager
// ---------------------------------------------------------------------------

/**
 * Manages reconnect logic and exposes observable connection state. One instance
 * per socket lifetime. PII (tokens, user data) NEVER appears in status updates.
 */
export class ReconnectManager {
  private readonly _listeners = new Set<StatusListener>();
  private _status: ConnectionStatus = 'connecting';
  private readonly _rooms: ActiveRooms = {
    hotels: new Map(),
    calls: new Map(),
  };

  constructor(
    private readonly socket: RealtimeSocket,
    private readonly queryClient: QueryClient,
  ) {
    this._attachSocketListeners();
  }

  // -------------------------------------------------------------------------
  // Room tracking — called by room subscription hooks
  // -------------------------------------------------------------------------

  trackHotelRoom(tenantId: string, hotelId: string): void {
    if (!this._rooms.hotels.has(tenantId)) {
      this._rooms.hotels.set(tenantId, new Set());
    }
    this._rooms.hotels.get(tenantId)!.add(hotelId);
  }

  untrackHotelRoom(tenantId: string, hotelId: string): void {
    this._rooms.hotels.get(tenantId)?.delete(hotelId);
  }

  trackCallRoom(tenantId: string, hotelId: string, callId: string): void {
    this._rooms.calls.set(callId, { tenantId, hotelId });
  }

  untrackCallRoom(callId: string): void {
    this._rooms.calls.delete(callId);
  }

  // -------------------------------------------------------------------------
  // Status observable
  // -------------------------------------------------------------------------

  get status(): ConnectionStatus {
    return this._status;
  }

  subscribe(listener: StatusListener): () => void {
    this._listeners.add(listener);
    return () => {
      this._listeners.delete(listener);
    };
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  destroy(): void {
    this.socket.off('connect', this._onConnect);
    this.socket.off('disconnect', this._onDisconnect);
    this.socket.off('reconnect_attempt', this._onReconnectAttempt);
    this.socket.off('connect_error', this._onConnectError);
    this._listeners.clear();
  }

  // -------------------------------------------------------------------------
  // Private
  // -------------------------------------------------------------------------

  private _emit(status: ConnectionStatus): void {
    this._status = status;
    for (const listener of this._listeners) {
      listener(status);
    }
  }

  private _onConnect = (): void => {
    this._emit('connected');
    this._resubscribeAll();
    this._invalidateAll();
  };

  private _onDisconnect = (): void => {
    this._emit('disconnected');
  };

  private _onReconnectAttempt = (): void => {
    this._emit('reconnecting');
  };

  private _onConnectError = (): void => {
    // Log without PII — the error object may contain token details in message.
    console.warn('[realtime] connection error — check token validity');
    this._emit('disconnected');
  };

  private _attachSocketListeners(): void {
    this.socket.on('connect', this._onConnect);
    this.socket.on('disconnect', this._onDisconnect);
    this.socket.on('reconnect_attempt', this._onReconnectAttempt);
    this.socket.on('connect_error', this._onConnectError);
  }

  /**
   * Re-subscribes all active rooms after a reconnect.
   * The token is refreshed automatically by the `auth` callback in `socket.ts`.
   */
  private _resubscribeAll(): void {
    for (const [_tenantId, hotelIds] of this._rooms.hotels) {
      for (const hotelId of hotelIds) {
        subscribeHotelRoom(this.socket, hotelId);
      }
    }

    for (const [callId, { hotelId }] of this._rooms.calls) {
      subscribeCallRoom(this.socket, hotelId, callId);
    }
  }

  /**
   * Invalidates all hotel-scoped TanStack Query caches to force a resync.
   * Uses `hotelScopePrefix` — no hand-built keys.
   */
  private _invalidateAll(): void {
    for (const [tenantId, hotelIds] of this._rooms.hotels) {
      for (const hotelId of hotelIds) {
        invalidateHotelScope(this.queryClient, tenantId, hotelId);
      }
    }
  }
}
